import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";

async function fetcher(url) {
  let page = 1;
  let per_page = 100;
  let results = [];
  let response, totalPages;
  response = await axios
    .get(`${url}?page=${page}&per_page=${per_page}`)
    .catch((err) => console.error(err));
  totalPages = response.headers["x-wp-totalpages"];
  results = results.concat(response.data);
  if (page < totalPages) {
    for (let i = page + 1; i <= totalPages; i++) {
      response = await axios
        .get(`${url}?page=${i}&per_page=${per_page}`)
        .catch((err) => console.error(err));
      results = results.concat(response.data);
    }
  }
  return results;
}

function countCountries(gips, selectedCountries = []) {
  const countries = new Set();
  gips.forEach((g) => {
    g["taxonomy-country"]
      .filter(
        (c) => selectedCountries.length === 0 || selectedCountries.includes(c),
      )
      .forEach((c) => countries.add(c));
  });
  return countries.size;
}

function countCompanies(gips) {
  const companies = new Set();
  gips.forEach((g) => {
    g["gpi-company"]?.forEach((c) => companies.add(c));
  });
  return companies.size;
}

export default function useGip() {
  const isMobile = window.innerWidth <= 420;
  const isLarge = window.screen.width >= 1550;
  const [gips, setGips] = useState([]);
  const [countriesCount, setCountriesCount] = useState(0);
  const [companiesCount, setCompaniesCount] = useState(0);
  const [gipsCount, setGipsCount] = useState(0);
  const [activeProject, setActiveProject] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [showProjectList, setShowProjectList] = useState(
    isMobile ? false : true,
  );
  const [markerClicked, setMarkerClicked] = useState(false);
  const [hoverProject, setHoverProject] = useState(null);
  const [map, setMap] = useState(null);
  const { data: allGips, error } = useSWR(
    `${window?.__env__?.WP_SOURCE}/wp-json/wp/v2/gips`,
    fetcher,
  );
  const minZoom = isLarge ? 5 : 4;
  const defaultMapSettings = {
    center: isMobile
      ? [48.505, 5.09]
      : isLarge
        ? [48.505, 18.09]
        : [53.505, 18.09],
    zoom: minZoom,
    minZoom,
    maxZoom: 7,
  };

  const processQuery = useCallback(
    ({ countries, technologies, statuses }) => {
      if (!allGips?.length) {
        return;
      }
      let results = [...allGips];
      if (statuses.length) {
        results = results.filter((gpi) =>
          statuses.some((status) => gpi["gpi-statuses"].includes(status)),
        );
      }
      if (countries.length) {
        results = results.filter((gpi) =>
          countries.some((country) =>
            gpi["taxonomy-country"].includes(country),
          ),
        );
        setSelectedCountries(countries);
      } else {
        setSelectedCountries([]);
      }
      if (technologies.length) {
        results = results.filter((gpi) =>
          technologies.some((technology) =>
            gpi["gpi-tech"].includes(technology),
          ),
        );
      }

      setGips([...results]);
    },
    [allGips],
  );

  const handleSetActiveProject = (projectId, countryId) => {
    if (projectId && countryId) {
      setActiveProject({ projectId, countryId });
    } else {
      setActiveProject(null);
    }
  };

  const handleSetHoverProject = (projectId, countryId) => {
    if (projectId && countryId) {
      setHoverProject({ projectId, countryId });
    } else {
      setHoverProject(null);
    }
  };

  const resetMap = () => {
    map.flyTo(defaultMapSettings.center, defaultMapSettings.zoom);
  };

  useEffect(() => {
    if (allGips?.length) {
      setGips([...allGips]);
    }
  }, [allGips]);

  useEffect(() => {
    setGipsCount(gips.length);
    setCountriesCount(countCountries(gips, selectedCountries));
    setCompaniesCount(countCompanies(gips));
  }, [gips, selectedCountries]);
  return {
    gips,
    error,
    loading: !error && allGips == undefined,
    gipsCount,
    countriesCount,
    companiesCount,
    processQuery,
    setActiveProject: handleSetActiveProject,
    activeProject,
    showProjectList,
    setShowProjectList: setShowProjectList,
    hoverProject,
    setHoverProject: handleSetHoverProject,
    map,
    setMap,
    resetMap,
    defaultMapSettings,
    isMobile,
    markerClicked,
    setMarkerClicked: setMarkerClicked,
  };
}
